/**
 * Parsers for the rating / box office / awards strings that come from OMDb
 * ("7.3", "91%", "69/100", "$22,847,564", "Won 1 Oscar. 10 wins & 5
 * nominations"). They return null for "N/A" and anything unparseable.
 * Pure functions, safe to use on the client.
 */

const NOT_AVAILABLE = "N/A";

/** The trimmed value, or null when missing / "N/A". */
function present(value: string | null | undefined): string | null {
  const trimmed = value?.trim();

  return trimmed && trimmed !== NOT_AVAILABLE ? trimmed : null;
}

/** IMDb / TMDB / Cage MDB style "7.3" (or "7.3/10") -> 7.3 */
export function parseScore(value: string | null | undefined): number | null {
  const match = present(value)?.match(/^(\d+(?:\.\d+)?)(?:\s*\/\s*10)?$/);

  if (!match) return null;
  const score = Number(match[1]);

  return score >= 0 && score <= 10 ? score : null;
}

/** Rotten Tomatoes "91%" -> 91 */
export function parsePercentage(
  value: string | null | undefined,
): number | null {
  const match = present(value)?.match(/^(\d{1,3})\s*%$/);

  if (!match) return null;
  const percent = Number(match[1]);

  return percent <= 100 ? percent : null;
}

/** Metacritic "69/100" (or "69") -> 69 */
export function parseMetacritic(
  value: string | null | undefined,
): number | null {
  const match = present(value)?.match(/^(\d{1,3})(?:\s*\/\s*100)?$/);

  if (!match) return null;
  const score = Number(match[1]);

  return score <= 100 ? score : null;
}

/** "$22,847,564" -> 22847564 */
export function parseBoxOffice(
  value: string | null | undefined,
): number | null {
  const cleaned = present(value)?.replace(/[$,\s]/g, "");

  if (!cleaned || !/^\d+(\.\d+)?$/.test(cleaned)) return null;

  return Number(cleaned);
}

/** "138 min" -> 138 */
export function parseRuntime(value: string | null | undefined): number | null {
  const match = present(value)?.match(/^(\d+)\s*min$/i);

  if (!match) return null;
  const minutes = Number(match[1]);

  return minutes > 0 ? minutes : null;
}

export interface AwardCounts {
  oscars: number;
  wins: number;
  nominations: number;
}

/**
 * "Won 1 Oscar. 10 wins & 5 nominations" -> { oscars: 1, wins: 10, nominations: 5 }
 * Returns null when there are no awards at all.
 */
export function parseAwards(
  value: string | null | undefined,
): AwardCounts | null {
  const text = present(value);

  if (!text) return null;

  const oscarWins = text.match(/Won (\d+) Oscars?/i);
  const wins = text.match(/(\d+)\s+wins?/i);
  const nominations = text.match(/(\d+)\s+nominations?/i);
  const counts = {
    oscars: oscarWins ? Number(oscarWins[1]) : 0,
    wins: wins ? Number(wins[1]) : 0,
    nominations: nominations ? Number(nominations[1]) : 0,
  };

  return counts.oscars + counts.wins + counts.nominations > 0 ? counts : null;
}

/** Compare award counts hierarchically: Oscars, then wins, then nominations. */
export function compareAwards(a: AwardCounts, b: AwardCounts): number {
  return (
    a.oscars - b.oscars || a.wins - b.wins || a.nominations - b.nominations
  );
}
